import React, { useState, useEffect } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { FiFileText } from "react-icons/fi";
import { useReportTemplateActions } from "../hooks/useReportTemplateActions";
import { UpdateTemplateRequest } from "../types/reportTemplateRequests";
import { ReportTemplate } from "../types/reportTemplate";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { ErrorResponse } from "@core/types/errorResponse";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import { useSearchParams } from "react-router-dom";
import InfoTooltip from "@core/components/InfoTooltip";
import { useScopeId } from "@core/hooks/useRouteParamId";

type UpdateTemplateFormState = {
    report_template_id: number;
    report_template_name: string;
    description?: string | null;
};

type FieldErrors = Partial<Record<keyof UpdateTemplateFormState, string[]>>;

const EMPTY_FORM: UpdateTemplateFormState = {
    report_template_id: 0,
    report_template_name: "",
    description: "",
};

const UpdateTemplateModal: React.FC<{
    show: boolean;
    onHide: () => void;
    template: ReportTemplate | null;
    onUpdated?: () => void;
}> = ({ show, onHide, template, onUpdated }) => {
    const [searchParams] = useSearchParams();
    const resolvedDepartmentId = Number(searchParams.get("did"));
    const resolvedNationId = useScopeId() ?? 0;

    const [form, setForm] = useState<UpdateTemplateFormState>(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState<FieldErrors>({});
    const showError = useApiError();
    const showValidation = useErrorModalStore((s) => s.showValidation);
    const [, setError] = useState<string | null>(null);
    const { busy, doTemplateUpdate } = useReportTemplateActions();

    // when the incoming template changes, rehydrate form with safe defaults
    useEffect(() => {
        if (template) {
            setForm({
                report_template_id: template.report_template_id,
                report_template_name: template.report_template_name ?? "",
                description: template.description ?? "",
            });
        } else {
            setForm(EMPTY_FORM);
        }
    }, [template]);

    // Check if form is unchanged
    const isUnchanged = (form: UpdateTemplateFormState, original: ReportTemplate | null | undefined): boolean => {

        // If no original to compare to, consider it changed
        if (!original) return false;

        // Compare relevant fields
        return (
            (form.report_template_name ?? "") === (original.report_template_name ?? "") &&
            (form.description ?? "") === (original.description ?? "")
        );
    };

    // Handle modal close
    const handleClose = () => {
        setFormErrors({});
        setError(null);
        onHide();
    };

    // Handle form field changes
    const handleChange = (field: keyof UpdateTemplateFormState) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        let value: string | number = e.target.value;

        setForm(f => ({
            ...f,
            [field]: value
        }));
    };

    // Handle form submission
    const handleSubmit = async () => {
        const messages: string[] = [];
        if (!template) messages.push("No template selected.");
        if (!form.report_template_id) messages.push("Template ID is missing.");
        if (!form.report_template_name?.trim()) messages.push("Template name is required.");
        if (template && isUnchanged(form, template)) messages.push("No changes to save.");
        if (messages.length > 0) {
            showValidation(messages);
            return;
        }

        try {
            setFormErrors({});
            setError(null);

            const payload: UpdateTemplateRequest = {
                report_template_name: form.report_template_name,
                description: form.description,
            };

            const response = await doTemplateUpdate(resolvedNationId, resolvedDepartmentId, form.report_template_id, payload);

            if (response.isSuccess()) {
                showSuccess(response.getData()?.message);
                onHide();
                setForm(EMPTY_FORM);
                onUpdated?.();
            } else {
                // Handle error
                const error = response.getError() as ErrorResponse;
                setFormErrors((error?.errors as FieldErrors) ?? {});
                showError(error);
            }

        } catch (error) {
            showError(error as ErrorResponse);
        }
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            centered
            size="lg"
            data-testid="update-template-modal"
        >
            <Modal.Header closeButton className="border-0 pb-0" data-testid="update-template-modal-header">
                <Modal.Title className="fw-semibold">
                    <FiFileText className="me-2" aria-hidden />
                    Update Report Template
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3 pb-4">
                <Form>
                    <Form.Group className="mb-3" controlId="templateName">
                        <Form.Label>Template Name <span className="text-danger">*</span><InfoTooltip text="Name of this report template as it appears to users when they choose a template to fill." /></Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter Template name"
                            value={form.report_template_name ?? ""}
                            onChange={handleChange("report_template_name")}
                            isInvalid={!!formErrors.report_template_name}
                        />
                        <Form.Control.Feedback type="invalid" role="alert" data-testid="template-name-error">
                            {formErrors.report_template_name?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="templateDescription">
                        <Form.Label>Description<InfoTooltip text="Optional description of what this template is used for." /></Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Enter Template description"
                            value={form.description ?? ""}
                            onChange={handleChange("description")}
                            isInvalid={!!formErrors.description}
                        />
                        <Form.Control.Feedback type="invalid" role="alert" data-testid="template-description-error">
                            {formErrors.description?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <div className="d-flex justify-content-end mt-3">
                        <Button
                            variant="primary"
                            size="lg"
                            className="btn-action"
                            onClick={handleSubmit}
                            disabled={busy}
                            data-testid="update-template-submit-button"
                            aria-busy={busy}
                        >
                            {busy ? (
                                <>
                                    <Spinner
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                    />
                                    Updating Template...
                                </>
                            ) : (
                                "Update Template"
                            )}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default UpdateTemplateModal;